import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Slider,
  TextField,
  Grid,
} from '@material-ui/core';
import { CloudUpload as UploadIcon } from '@material-ui/icons';
import { useStyles } from './style';

interface CommunityLoaderProps {
  speedMultiplier: number;
  onSpeedChange: (speed: number) => void;
}

const CommunityLoader: React.FC<CommunityLoaderProps> = ({
  speedMultiplier,
  onSpeedChange,
}) => {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.endsWith('.json')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result as string);
          setPreview(json);
        } catch {
          setPreview(null);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Paper className={classes.card} style={{ padding: 24 }}>
      <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
        Community Loader
      </Typography>

      <Grid container spacing={3}>
        {/* File Upload */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Community JSON
          </Typography>
          <input
            accept=".json"
            id="fixture-file-input"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <label htmlFor="fixture-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              className={classes.actionButton}
            >
              Select JSON File
            </Button>
          </label>
          {selectedFile && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Selected: {selectedFile.name}
            </Typography>
          )}
        </Grid>

        {/* Speed Control */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            Speed Multiplier: {speedMultiplier.toFixed(1)}x
          </Typography>
          <Box className={classes.speedSlider}>
            <Slider
              value={speedMultiplier}
              onChange={(_, val) => onSpeedChange(val as number)}
              min={0.1}
              max={5.0}
              step={0.1}
              marks={[
                { value: 0.5, label: '0.5x' },
                { value: 1, label: '1x' },
                { value: 2, label: '2x' },
                { value: 5, label: '5x' },
              ]}
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            Lower = slower (easier to watch). Higher = faster execution.
          </Typography>
        </Grid>

        {/* Preview */}
        {preview && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Fixture Preview
            </Typography>
            <Box
              style={{
                backgroundColor: '#F9FAFB',
                padding: 16,
                borderRadius: 8,
                fontFamily: 'monospace',
                fontSize: 13,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              <div><strong>Test ID:</strong> {preview.testId}</div>
              <div><strong>Organization:</strong> {preview.organizationName}</div>
              <div><strong>Community:</strong> {preview.community?.communityName} ({preview.community?.communityType})</div>
              <div><strong>Channels:</strong> {preview.channels?.length || 0}</div>
              <div><strong>Users:</strong> {preview.users?.length || 0}</div>
              <div><strong>Posts:</strong> {preview.posts?.length || 0}</div>
              <div><strong>Chat Messages:</strong> {preview.chatMessages?.length || 0}</div>
              <div><strong>Invitations:</strong> {preview.invitations?.length || 0}</div>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            To run a fixture, use the CLI: <code>cd e2e && npx ts-node scripts/run-suite.ts --fixture your-fixture.json</code>
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CommunityLoader;
